// viewModalFilters.js

import { enumOptionValues } from 'utilities/EnumOptions';
import { normalizeSchemaFieldType, isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// These constants define the operator groups used by the filter UI and
// the normalization logic that prepares filters for saving.
export const EMPTY_VALUE_OPERATORS = new Set(['is_empty', 'is_not_empty']);
export const RELATIVE_DATE_OPERATORS = new Set([
    'within_past',
    'within_next',
    'older_than',
    'is_overdue',
    'is_today',
    'is_this',
]);
export const RELATIVE_DATERANGE_OPERATORS = new Set([
    'overlaps_past',
    'overlaps_next',
    'is_active',
    'overlaps_this',
]);
export const VALUELESS_OPERATORS = new Set([
    'is_empty',
    'is_not_empty',
    'is_overdue',
    'is_today',
    'is_active',
    'is_me',
    'is_not_me',
]);
export const ENUM_MULTI_OPERATORS = new Set(['is_any_of', 'is_none_of']);

export const normalizeFilterJoin = (join) => (
    String(join || '').toLowerCase() === 'or' ? 'or' : 'and'
);

// Units are used by relative date filters.
export const RELATIVE_DATE_UNITS = [
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'years', label: 'Years' },
];
export const RELATIVE_CALENDAR_UNITS = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
];

// Normalize text inputs to the unit names used in the UI.
export const normalizeRelativeUnit = (unit) => {
    const normalized = String(unit || '').toLowerCase();
    if (normalized === 'day' || normalized === 'days') return 'days';
    if (normalized === 'week' || normalized === 'weeks') return 'weeks';
    if (normalized === 'month' || normalized === 'months') return 'months';
    if (normalized === 'year' || normalized === 'years') return 'years';
    return '';
};

export const normalizeCalendarUnit = (unit) => {
    const normalized = String(unit || '').toLowerCase();
    if (normalized === 'week' || normalized === 'weeks') return 'week';
    if (normalized === 'month' || normalized === 'months') return 'month';
    if (normalized === 'year' || normalized === 'years') return 'year';
    return '';
};

// Field type utilities keep the UI lean as new types are added.
export const resolveFieldType = (fieldDef, fallbackType = 'string') => (
    normalizeSchemaFieldType(fieldDef?.type || fallbackType || 'string', 'string')
);

export const isDateLikeFieldType = (fieldType) => (
    fieldType === 'annualdate' || fieldType === 'date' || fieldType === 'daterange' || fieldType === 'datetime' || fieldType === 'datetimerange'
);

export const isRelativeTemporalOperator = (fieldType, operator) => {
    if (fieldType === 'annualdate') {
        return RELATIVE_DATE_OPERATORS.has(operator);
    }
    if (fieldType === 'date') {
        return RELATIVE_DATE_OPERATORS.has(operator);
    }
    if (fieldType === 'daterange' || fieldType === 'datetimerange') {
        return RELATIVE_DATERANGE_OPERATORS.has(operator)
            || RELATIVE_DATE_OPERATORS.has(operator);
    }
    if (fieldType === 'datetime') {
        return RELATIVE_DATE_OPERATORS.has(operator);
    }
    return false;
};

export const operatorNeedsValue = (fieldType, operator) => {
    if (fieldType === 'annualdate') {
        return operator === 'within_past' || operator === 'within_next' || operator === 'older_than';
    }
    if (fieldType === 'date') {
        return operator === 'within_past' || operator === 'within_next' || operator === 'older_than';
    }
    if (fieldType === 'daterange' || fieldType === 'datetimerange') {
        return operator === 'overlaps_past'
            || operator === 'overlaps_next'
            || operator === 'within_past'
            || operator === 'within_next';
    }
    if (fieldType === 'datetime') {
        return operator === 'within_past' || operator === 'within_next' || operator === 'older_than';
    }
    return false;
};

export const operatorNeedsUnit = (fieldType, operator) => {
    if (fieldType === 'annualdate') {
        return operator === 'within_past' || operator === 'within_next' || operator === 'older_than' || operator === 'is_this';
    }
    if (fieldType === 'date') {
        return operator === 'within_past' || operator === 'within_next' || operator === 'older_than' || operator === 'is_this';
    }
    if (fieldType === 'daterange' || fieldType === 'datetimerange') {
        return operator === 'overlaps_past'
            || operator === 'overlaps_next'
            || operator === 'overlaps_this'
            || operator === 'within_past'
            || operator === 'within_next'
            || operator === 'is_this';
    }
    if (fieldType === 'datetime') {
        return operator === 'within_past' || operator === 'within_next' || operator === 'older_than' || operator === 'is_this';
    }
    return false;
};

const stringOperators = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const numberOperators = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'gt', label: 'greater than' },
    { value: 'gte', label: 'is at least' },
    { value: 'lt', label: 'less than' },
    { value: 'lte', label: 'is at most' },
    { value: 'between', label: 'is between' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const dateOperators = [
    { value: 'within_past', label: 'within past' },
    { value: 'within_next', label: 'within next' },
    { value: 'older_than', label: 'older than' },
    { value: 'is_overdue', label: 'is overdue' },
    { value: 'is_today', label: 'is today' },
    { value: 'is_this', label: 'is this' },
];

const dateRangeOperators = [
    { value: 'overlaps_past', label: 'overlaps past' },
    { value: 'overlaps_next', label: 'overlaps next' },
    { value: 'is_active', label: 'is active now' },
    { value: 'overlaps_this', label: 'overlaps this' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const booleanOperators = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
];

const measurementOperators = [
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const enumOperators = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' },
    { value: 'is_any_of', label: 'is any of' },
    { value: 'is_none_of', label: 'is none of' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const tagOperators = [
    { value: 'is_any_of', label: 'is any of' },
    { value: 'is_none_of', label: 'is none of' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const peopleOperators = [
    { value: 'is_me', label: 'contains me' },
    { value: 'is_not_me', label: 'does not contain me' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
];

const coerceNumber = (value) => {
    const numeric = value === '' || value == null ? null : Number(value);
    return Number.isNaN(numeric) ? null : numeric;
};

const coercePositiveNumber = (value) => {
    const numeric = coerceNumber(value);
    if (numeric == null) return null;
    return numeric > 0 ? numeric : null;
};

const normalizeNumberBetweenValue = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        const numeric = coerceNumber(value);
        return { min: numeric, max: null };
    }

    return {
        min: coerceNumber(value.min),
        max: coerceNumber(value.max),
    };
};

const defaultDateValue = (operator) => {
    if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
        return { value: 1, unit: 'days' };
    }
    if (operator === 'is_this') {
        return { value: null, unit: 'week' };
    }
    return { value: null, unit: null };
};

const defaultDateRangeValue = (operator) => {
    if (operator === 'overlaps_past' || operator === 'overlaps_next') {
        return { value: 1, unit: 'days' };
    }
    if (operator === 'overlaps_this') {
        return { value: null, unit: 'week' };
    }
    return { value: null, unit: null };
};

const isEnumMultiOperator = (operator) => ENUM_MULTI_OPERATORS.has(operator);
const isEnumLikeType = (fieldType) => (
    isEnumCompatibleFieldType(fieldType)
    || fieldType === 'multiselect'
    || fieldType === 'tags'
    || fieldType === 'relation'
);

const normalizeEnumValue = (value, operator) => {
    if (isEnumMultiOperator(operator)) {
        if (Array.isArray(value)) return value.filter((item) => item !== '');
        if (value == null || value === '') return [];
        return [value];
    }

    if (Array.isArray(value)) return value[0] ?? '';
    return value ?? '';
};

const buildEnumDefaultValue = (fieldDef, operator) => {
    const values = enumOptionValues(fieldDef?.options || []);
    const firstValue = values[0] || '';
    if (isEnumMultiOperator(operator)) {
        return { value: firstValue ? [firstValue] : [], unit: null };
    }
    return { value: firstValue, unit: null };
};

// Field type registry: add new types here without touching the UI layer.
const FIELD_TYPE_SPECS = {
    string: {
        operators: stringOperators,
        valueInputKind: 'text',
        defaultValue: () => ({ value: '', unit: null }),
        normalizeValue: (value) => value,
    },
    number: {
        operators: numberOperators,
        valueInputKind: 'number',
        defaultValue: (_fieldDef, operator) => (
            operator === 'between'
                ? { value: { min: '', max: '' }, unit: null }
                : { value: '', unit: null }
        ),
        normalizeValue: (value, operator) => (
            operator === 'between' ? normalizeNumberBetweenValue(value) : coerceNumber(value)
        ),
    },
    money: {
        operators: numberOperators,
        valueInputKind: 'number',
        defaultValue: (_fieldDef, operator) => (
            operator === 'between'
                ? { value: { min: '', max: '' }, unit: null }
                : { value: '', unit: null }
        ),
        normalizeValue: (value, operator) => (
            operator === 'between' ? normalizeNumberBetweenValue(value) : coerceNumber(value)
        ),
    },
    currency: {
        operators: enumOperators,
        valueInputKind: 'text',
        defaultValue: () => ({ value: 'USD', unit: null }),
        normalizeValue: (value) => String(value || '').trim().toUpperCase(),
    },
    percentage: {
        operators: numberOperators,
        valueInputKind: 'number',
        defaultValue: (_fieldDef, operator) => (
            operator === 'between'
                ? { value: { min: '', max: '' }, unit: null }
                : { value: '', unit: null }
        ),
        normalizeValue: (value, operator) => (
            operator === 'between' ? normalizeNumberBetweenValue(value) : coerceNumber(value)
        ),
    },
    boolean: {
        operators: booleanOperators,
        valueInputKind: 'boolean',
        defaultValue: () => ({ value: true, unit: null }),
        normalizeValue: (value) => value === true || value === 'true',
    },
    measurement: {
        operators: measurementOperators,
        valueInputKind: 'none',
        defaultValue: () => ({ value: null, unit: null }),
        normalizeValue: () => null,
    },
    enum: {
        operators: enumOperators,
        valueInputKind: 'enum',
        defaultValue: (fieldDef, operator) => buildEnumDefaultValue(fieldDef, operator),
        normalizeValue: normalizeEnumValue,
    },
  pipeline: {
        operators: enumOperators,
        valueInputKind: 'enum',
        defaultValue: (fieldDef, operator) => buildEnumDefaultValue(fieldDef, operator),
        normalizeValue: normalizeEnumValue,
    },
    multiselect: {
        operators: tagOperators,
        valueInputKind: 'enum',
        defaultValue: (fieldDef, operator) => buildEnumDefaultValue(fieldDef, operator),
        normalizeValue: normalizeEnumValue,
    },
    tags: {
        operators: tagOperators,
        valueInputKind: 'enum',
        defaultValue: (fieldDef, operator) => buildEnumDefaultValue(fieldDef, operator),
        normalizeValue: normalizeEnumValue,
    },
    people: {
        operators: peopleOperators,
        valueInputKind: 'none',
        defaultValue: () => ({ value: null, unit: null }),
        normalizeValue: () => null,
    },
    relation: {
        operators: enumOperators,
        valueInputKind: 'relation',
        defaultValue: (_fieldDef, operator) => (
            isEnumMultiOperator(operator)
                ? { value: [], unit: null }
                : { value: '', unit: null }
        ),
        normalizeValue: normalizeEnumValue,
    },
    annualdate: {
        operators: dateOperators,
        valueInputKind: 'date',
        defaultValue: (_fieldDef, operator) => defaultDateValue(operator),
        normalizeValue: (value, operator) => {
            if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
                return coercePositiveNumber(value);
            }
            return value ?? null;
        },
    },
    date: {
        operators: dateOperators,
        valueInputKind: 'date',
        defaultValue: (_fieldDef, operator) => defaultDateValue(operator),
        normalizeValue: (value, operator) => {
            if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
                return coercePositiveNumber(value);
            }
            return value ?? null;
        },
    },
    datetime: {
        operators: dateOperators,
        valueInputKind: 'date',
        defaultValue: (_fieldDef, operator) => defaultDateValue(operator),
        normalizeValue: (value, operator) => {
            if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
                return coercePositiveNumber(value);
            }
            return value ?? null;
        },
    },
    daterange: {
        operators: dateRangeOperators,
        valueInputKind: 'date',
        defaultValue: (_fieldDef, operator) => defaultDateRangeValue(operator),
        normalizeValue: (value, operator) => {
            if (
                operator === 'overlaps_past'
                || operator === 'overlaps_next'
                || operator === 'within_past'
                || operator === 'within_next'
            ) {
                return coercePositiveNumber(value);
            }
            return value ?? null;
        },
    },
    datetimerange: {
        operators: dateRangeOperators,
        valueInputKind: 'date',
        defaultValue: (_fieldDef, operator) => defaultDateRangeValue(operator),
        normalizeValue: (value, operator) => {
            if (
                operator === 'overlaps_past'
                || operator === 'overlaps_next'
                || operator === 'within_past'
                || operator === 'within_next'
            ) {
                return coercePositiveNumber(value);
            }
            return value ?? null;
        },
    },
};

const getFieldTypeSpec = (fieldType) => (
    FIELD_TYPE_SPECS[fieldType] || FIELD_TYPE_SPECS.string
);

// Operator options are chosen by field type.
export const getFilterOperatorOptions = (fieldType) => (
    getFieldTypeSpec(fieldType).operators
);

// Value input kind tells the row which input control to render.
export const getValueInputKind = (fieldType) => (
    getFieldTypeSpec(fieldType).valueInputKind
);

// Builds a new filter row based on the first available field.
export const buildDefaultFilter = (filterableFields) => {
    const firstField = filterableFields[0];
    const fieldKey = firstField?.name || firstField?.key || '';
    return buildFilterForFieldKey(fieldKey, firstField);
};

// Builds a new filter row using the selected field definition.
export const buildFilterForFieldKey = (fieldKey, fieldDef) => {
    const fieldType = resolveFieldType(fieldDef, 'string');
    const operatorOptions = getFilterOperatorOptions(fieldType);
    const operator = operatorOptions[0]?.value || 'is';
    const { value, unit } = getFieldTypeSpec(fieldType).defaultValue(fieldDef, operator);
    return { field: fieldKey, operator, value, unit, joinToNext: 'and' };
};

// Updates a filter when its operator changes, handling date logic safely.
export const applyOperatorChange = ({ fieldType, operator, currentFilter, fieldDef }) => {
    if (isEnumLikeType(fieldType)) {
        const { value: defaultValue } = buildEnumDefaultValue(fieldDef, operator);
        if (isEnumMultiOperator(operator)) {
            const currentValues = Array.isArray(currentFilter?.value)
                ? currentFilter.value
                : normalizeEnumValue(currentFilter?.value, operator);
            return {
                ...currentFilter,
                operator,
                value: currentValues.length ? currentValues : defaultValue,
                unit: null,
            };
        }

        const nextValue = Array.isArray(currentFilter?.value)
            ? currentFilter.value[0] ?? defaultValue
            : currentFilter?.value ?? defaultValue;
        return { ...currentFilter, operator, value: nextValue, unit: null };
    }

    const isNumericFieldType = fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage';

    if (isNumericFieldType && operator === 'between') {
        const currentValue = currentFilter?.value;
        const min = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
            ? (currentValue.min ?? '')
            : (currentValue ?? '');
        const max = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
            ? (currentValue.max ?? '')
            : '';

        return {
            ...currentFilter,
            operator,
            value: { min, max },
            unit: null,
        };
    }

    if (isNumericFieldType && currentFilter?.operator === 'between') {
        const currentValue = currentFilter?.value;
        const fallbackValue = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
            ? (currentValue.min ?? '')
            : '';

        return {
            ...currentFilter,
            operator,
            value: fallbackValue,
            unit: null,
        };
    }

    if (!isDateLikeFieldType(fieldType)) {
        return { ...currentFilter, operator, unit: null };
    }

    if (fieldType === 'daterange' || fieldType === 'datetimerange') {
        if (
            operator === 'overlaps_past'
            || operator === 'overlaps_next'
            || operator === 'within_past'
            || operator === 'within_next'
        ) {
            return {
                ...currentFilter,
                operator,
                value: currentFilter.value == null || currentFilter.value === ''
                    ? 1
                    : currentFilter.value,
                unit: normalizeRelativeUnit(currentFilter.unit) || 'days',
            };
        }

        if (operator === 'overlaps_this' || operator === 'is_this') {
            return {
                ...currentFilter,
                operator,
                value: null,
                unit: normalizeCalendarUnit(currentFilter.unit) || 'week',
            };
        }

        if (operator === 'is_active' || operator === 'is_overdue' || operator === 'is_today') {
            return {
                ...currentFilter,
                operator,
                value: null,
                unit: null,
            };
        }

        return { ...currentFilter, operator, unit: null };
    }

    if (operator === 'within_past' || operator === 'within_next' || operator === 'older_than') {
        return {
            ...currentFilter,
            operator,
            value: currentFilter.value == null || currentFilter.value === ''
                ? 1
                : currentFilter.value,
            unit: normalizeRelativeUnit(currentFilter.unit) || 'days',
        };
    }

    if (operator === 'is_this') {
        return {
            ...currentFilter,
            operator,
            value: null,
            unit: normalizeCalendarUnit(currentFilter.unit) || 'week',
        };
    }

    if (operator === 'is_overdue' || operator === 'is_today') {
        return {
            ...currentFilter,
            operator,
            value: null,
            unit: null,
        };
    }

    return { ...currentFilter, operator, unit: null };
};

// Builds a new sort row based on the first available field.
export const buildDefaultSort = (filterableFields) => {
    const firstField = filterableFields[0];
    const fieldKey = firstField?.name || firstField?.key || '';
    return { field: fieldKey, direction: 'asc' };
};

const normalizeFilterValue = ({ fieldType, operator, value }) => {
    const spec = getFieldTypeSpec(fieldType);
    if (EMPTY_VALUE_OPERATORS.has(operator)) return null;

    if (isDateLikeFieldType(fieldType)) {
        if (
            operator === 'is_overdue'
            || operator === 'is_today'
            || operator === 'is_this'
            || operator === 'is_active'
            || operator === 'overlaps_this'
        ) {
            return null;
        }
        return spec.normalizeValue(value, operator);
    }

    return spec.normalizeValue(value, operator);
};

const shouldKeepUnit = ({ fieldType, operator }) => {
    if (!isDateLikeFieldType(fieldType)) return false;
    return operatorNeedsUnit(fieldType, operator);
};

const isValidFilter = ({ field, operator, value, unit }) => {
    if (!field || !operator) return false;
    if (VALUELESS_OPERATORS.has(operator) || EMPTY_VALUE_OPERATORS.has(operator)) return true;
    if (operator === 'is_overdue' || operator === 'is_today' || operator === 'is_active') return true;
    if (operator === 'is_this' || operator === 'overlaps_this') return Boolean(unit);
    if (
        operator === 'within_past'
        || operator === 'within_next'
        || operator === 'older_than'
        || operator === 'overlaps_past'
        || operator === 'overlaps_next'
    ) {
        return value != null && value !== '' && Boolean(unit);
    }
    if (operator === 'between') {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
        const min = coerceNumber(value.min);
        const max = coerceNumber(value.max);
        return min != null && max != null && min <= max;
    }
    if (ENUM_MULTI_OPERATORS.has(operator)) {
        return Array.isArray(value) && value.length > 0;
    }
    return value !== '' && value != null;
};

// Normalizes filters so the save service receives consistent data.
export const normalizeFilters = (filters, filterFieldMap) => (
    filters
        .map((filter) => {
            const fieldDef = filterFieldMap.get(filter.field);
            const fieldType = resolveFieldType(fieldDef, filter?.type);
            const operator = filter?.operator || '';
            const normalizedValue = normalizeFilterValue({
                fieldType,
                operator,
                value: filter?.value,
            });
            const unit = shouldKeepUnit({ fieldType, operator })
                ? filter?.unit || null
                : null;

            return {
                field: filter?.field,
                operator,
                value: normalizedValue,
                unit,
                type: fieldType,
                joinToNext: normalizeFilterJoin(filter?.joinToNext),
            };
        })
        .filter(isValidFilter)
);

// Normalizes sort rows by removing invalid entries.
export const normalizeSorts = (sorts) => (
    sorts
        .map((sort) => ({
            field: sort?.field,
            direction: sort?.direction || 'asc',
        }))
        .filter((sort) => sort.field && sort.direction)
);
